import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Typography,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import { MarkdownService } from '../services/markdownService';
import { sanitizeMarkdownContent, hasDangerousContent } from '../utils/contentSanitizer';

interface UploadedFile {
  originalFile: File;
  uploadedUrl: string;
}

interface EnhancedMarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  helperText?: string;
  required?: boolean;
  dataTestId?: string;
  onUpload?: (file: File) => Promise<string>; // Returns uploaded URL
  onSave?: () => void; // Called when user wants to save uploaded files
  onFilesChange?: (files: UploadedFile[]) => void; // Called when uploaded files change
  onAttachmentsChange?: (attachments: UploadedFile[]) => void; // Called when uploaded attachments change
}

const EnhancedMarkdownEditor: React.FC<EnhancedMarkdownEditorProps> = ({
  value,
  onChange,
  label = 'Markdown Content',
  helperText,
  required = false,
  dataTestId,
  onUpload,
  onSave,
  onFilesChange,
  onAttachmentsChange,
}) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<UploadedFile[]>([]);
  const [contentWarning, setContentWarning] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  // Check if content has data URLs (legacy) or uploaded URLs
  const hasDataUrls = value.includes('data:image/') || value.includes('data:video/');
  const hasUploadedUrls = value.includes('/statics/') || value.includes('http');

  // Notify parent when uploaded files change
  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(uploadedFiles);
    }
  }, [uploadedFiles, onFilesChange]);

  // Notify parent when uploaded attachments change
  useEffect(() => {
    if (onAttachmentsChange) {
      onAttachmentsChange(uploadedAttachments);
    }
  }, [uploadedAttachments, onAttachmentsChange]);

  // Handle file upload with API upload
  const handleFileUpload = useCallback(async (file: File) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      // Determine content type
      const contentType = file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('video/')
          ? 'video'
          : 'attachment';

      // Upload file to API
      const fileUrl = await MarkdownService.uploadFile(file, contentType);
      
      let markdownText = '';
      if (contentType === 'image') {
        markdownText = `\n![${file.name}](${fileUrl})\n`;
      } else if (contentType === 'video') {
        markdownText = `\n<video controls>\n  <source src="${fileUrl}" type="${file.type}">\n  Your browser does not support the video tag.\n</video>\n`;
      } else {
        // Attachment: standard markdown download link
        markdownText = `\n[${file.name}](${fileUrl})\n`;
      }

      // Add to content immediately
      const newContent = value + markdownText;
      onChange(newContent);

      // Track uploaded file
      setUploadedFiles(prev => [...prev, { originalFile: file, uploadedUrl: fileUrl }]);
      if (contentType === 'attachment') {
        setUploadedAttachments(prev => [...prev, { originalFile: file, uploadedUrl: fileUrl }]);
      }

      if (onSave) {
        onSave();
      }
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, [value, onChange, onSave]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      files.forEach(handleFileUpload);
    }
  }, [handleFileUpload]);

  // Handle paste events
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    
    // Check for image/video files first
    let hasMediaFiles = false;
    items.forEach(item => {
      if (item.type.startsWith('image/') || item.type.startsWith('video/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          handleFileUpload(file);
          hasMediaFiles = true;
        }
      }
    });

    // If no media files, check for text content but don't sanitize - just warn
    if (!hasMediaFiles) {
      const pastedText = e.clipboardData.getData('text/plain');
      if (pastedText) {
        // Check if pasted content contains dangerous patterns
        if (hasDangerousContent(pastedText)) {
          // Don't prevent paste, just show warning
          setContentWarning(
            t('help.dangerousContentWarning', 'Dangerous content detected. Scripts and unsafe HTML are not allowed. Preview will be disabled and content will be sanitized before saving.')
          );
        } else {
          // Content is safe, clear warning
          setContentWarning(null);
        }
      }
    }
  }, [handleFileUpload, value, onChange, t]);

  // Handle file input change (shared by all three inputs)
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach(handleFileUpload);
    
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  }, [handleFileUpload]);

  // Handle text field change
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Check for dangerous content and warn user (but don't modify content)
    if (hasDangerousContent(newValue)) {
      setContentWarning(
        t('help.dangerousContentWarning', 'Dangerous content detected. Scripts and unsafe HTML are not allowed. Preview will be disabled and content will be sanitized before saving.')
      );
    } else {
      setContentWarning(null);
    }
    
    // Always update the value as-is, don't sanitize
    onChange(newValue);
  }, [onChange, t]);

  return (
    <Box>
      {/* Image file input (Hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      {/* Video file input (Hidden) */}
      <input
        ref={videoInputRef}
        type="file"
        multiple
        accept="video/*"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      {/* Attachment file input (Hidden) */}
      <input
        ref={attachmentInputRef}
        type="file"
        multiple
        accept=".pdf,.xlsx,.xls,.docx,.doc,.csv,.zip,.pptx,.ppt,.txt"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Upload Controls */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
        <Tooltip title={t('help.uploadImage', 'Upload Image')}>
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <ImageIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('help.uploadVideo', 'Upload Video')}>
          <IconButton
            size="small"
            onClick={() => videoInputRef.current?.click()}
            disabled={isUploading}
          >
            <VideoIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('help.uploadAttachment', 'Upload Attachment (PDF, Word, Excel, …)')}>
          <IconButton
            size="small"
            onClick={() => attachmentInputRef.current?.click()}
            disabled={isUploading}
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Upload Progress */}
      {isUploading && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            {t('help.uploading', 'Adding file...')}
          </Typography>
        </Box>
      )}

      {/* Upload Error */}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {uploadError}
        </Alert>
      )}

      {/* Content Warning */}
      {contentWarning && (
        <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setContentWarning(null)}>
          {contentWarning}
        </Alert>
      )}

      {/* Data URL Warning (Legacy) */}
      {hasDataUrls && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('help.dataUrlWarning', 'This content contains legacy data URLs. Consider re-uploading files for better performance.')}
        </Alert>
      )}

      {/* Uploaded Files Success */}
      {hasUploadedUrls && !hasDataUrls && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {t('help.uploadedFilesSuccess', 'Files uploaded successfully to server.')}
        </Alert>
      )}

      {/* Uploaded Files Info */}
      {uploadedFiles.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('help.uploadedFiles', 'Uploaded files:')} {uploadedFiles.map(f => f.originalFile.name).join(', ')}
        </Alert>
      )}

      {/* Drag & Drop Zone */}
      <Box
        sx={{
          border: isDragOver ? '2px dashed #00B6BA' : '2px dashed transparent',
          borderRadius: 1,
          transition: 'border-color 0.2s ease',
        }}
      >
        <TextField
          label={label}
          value={value}
          onChange={handleTextChange}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPaste={handlePaste}
          multiline
          fullWidth
          required={required}
          helperText={
            helperText || 
            t('help.markdownEditorHelper', 'Drag & drop images/videos here, paste from clipboard, or type markdown content')
          }
          sx={{
            '& .MuiInputBase-root': {
              overflow: 'visible',
            },
            '& .MuiInputBase-input': {
              overflow: 'visible',
              minHeight: '200px',
            },
          }}
          data-testid={dataTestId}
        />
      </Box>

      {/* Instructions */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {t('help.uploadInstructions', 'Supported: Images (PNG, JPG, GIF), Videos (MP4, WebM), and Attachments (PDF, Word, Excel, CSV, ZIP). Files are uploaded to the server and URLs are embedded in markdown.')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t('help.uploadAttachmentInstructions', 'Supported: PDF, Excel, Word, CSV, ZIP. File is uploaded and a download link is embedded in the markdown.')}
      </Typography>
    </Box>
  );
};

export default EnhancedMarkdownEditor; 